import React, { useState } from "react";
import AppText from "./AppText";
import { appStrings } from "./strings";
import { UserPasskey } from "../types";
import Input from "../../presentational/Input";
import UnstyledButton from "../../presentational/UnstyledButton/UnstyledButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCancel, faRefresh, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { faEdit, faKey, faTrash } from "@fortawesome/pro-solid-svg-icons";

interface MfaPasskeyProps {
    isLoading: boolean;
    passkey: UserPasskey;
    renamePasskey: (passkey: UserPasskey) => void;
    deletePasskey: (key: string) => void;
}

const MfaPasskey: React.FC<MfaPasskeyProps> = ({ isLoading, passkey, renamePasskey, deletePasskey }) => {
    const [isRenaming, setIsRenaming] = useState(false);
    const [description, setDescription] = useState(passkey.description);

    function onDescriptionChange(e: React.ChangeEvent<HTMLInputElement>) {
        setDescription(e.target.value);
    }

    function rename() {
        setIsRenaming(true);
    }

    function cancel() {
        setDescription(passkey.description);
        setIsRenaming(false);
    }

    function save() {
        passkey.description = description;
        renamePasskey(passkey);
        setIsRenaming(false);
    }

    function remove() {
        if (confirm(appStrings["mfa-passkey-remove-confirm"])) {
            deletePasskey(passkey.id);
        }
    }

    function formatDate(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    }

    return (
        <div key={passkey.id} className="mfa-passkey">
            <div className="passkey-header">
                {!isLoading && !isRenaming && (
                    <>
                        <div className="passkey-name-container">
                            <span className="passkey-icon">
                                <FontAwesomeIcon icon={faKey} />
                            </span>
                            <span className="passkey-details">
                                <span className="passkey-name">
                                    {passkey.description || appStrings["mfa-passkey-default-description"]}
                                </span>
                                {passkey.dateCreated && (
                                    <div className="passkey-status">
                                        <p>
                                            <AppText textKey="mfa-passkey-date-created" />{" "}
                                            {formatDate(passkey.dateCreated)}
                                        </p>
                                    </div>
                                )}
                            </span>
                        </div>
                        <UnstyledButton
                            className="rename-button icon-button"
                            onClick={rename}
                            title={appStrings["mfa-passkey-rename-label"]}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </UnstyledButton>
                        <UnstyledButton
                            className="delete-button icon-button"
                            onClick={remove}
                            title={appStrings["mfa-passkey-remove-label"]}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </UnstyledButton>
                    </>
                )}
                {!isLoading && isRenaming && (
                    <>
                        <div className="passkey-name-container">
                            <Input
                                id={"description"}
                                fullWidth={true}
                                onChange={onDescriptionChange}
                                value={description}
                                autoFocus={true }
                                htmlAttributes={{ maxLength: 50 }}
                            ></Input>
                        </div>
                        <UnstyledButton
                            className="save-button editor-controls-button"
                            onClick={save}
                            title={appStrings["mfa-passkey-save-label"]}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </UnstyledButton>
                        <UnstyledButton
                            className="cancel-button icon-button"
                            onClick={cancel}
                            title={appStrings["mfa-passkey-cancel-label"]}
                        >
                            <FontAwesomeIcon icon={faCancel} />
                        </UnstyledButton>
                    </>
                )}
                {isLoading && (
                    <p>
                        <FontAwesomeIcon icon={faRefresh} spin />{" "}
                        {appStrings["mfa-passkey-fetching-data"]}
                    </p>
                )}
            </div>
        </div>
    );
};

export default MfaPasskey;
